import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { TextField, Button, FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { createCountAsync } from "../../../../slices/inventory/count";
import { getStorages } from "../../../../slices/inventory/storage";
import { getCountsAsync } from "../../../../slices/inventory/count";
import toast from "react-hot-toast";
import { ArrowLeftIcon, DocumentDuplicateIcon } from "@heroicons/react/24/outline";

export const NewInventoryCount = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const storagesState = useSelector((store) => store.inventory_storages);
  const storages = storagesState?.data || [];
  const countsState = useSelector((store) => store.inventory_counts);
  const counts = countsState?.data || [];
  
  const [isLoading, setIsLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [storageId, setStorageId] = useState("");
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyFromCountId, setCopyFromCountId] = useState("");
  const [copyAllProducts, setCopyAllProducts] = useState(false);
  
  useEffect(() => {
    // Always fetch storages to ensure we have the latest data
    dispatch(getStorages());
    dispatch(getCountsAsync({ per_page: 100 }));
  }, [dispatch]);

  const handleCreate = async () => {
    if (!title.trim()) {
      toast.error(t("inventory.count.title_required"));
      return;
    }
    
    if (!date) {
      toast.error(t("inventory.count.date_required"));
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        date: date,
        storage_id: storageId || null,
        active: true,
        finished: false,
      };

      const result = await dispatch(createCountAsync(payload));
      
      if (result.type?.endsWith('/fulfilled')) {
        const newCountId = result.payload?.id;
        toast.success(t("inventory.count.created"));
        
        // If copying from another count or all products
        if (copyFromCountId || copyAllProducts) {
          navigate(`/dashboard/inventory/counts/${newCountId}?copy_from=${copyFromCountId || 'all'}`);
        } else {
          navigate(`/dashboard/inventory/counts/${newCountId}`);
        }
      } else {
        const errorMessage = result.payload?.errors?.join(", ") || t("inventory.count.create_error");
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error creating count:", error);
      toast.error(t("inventory.count.create_error"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DashboardHeader
      title={t("inventory.count.create_new")}
      subtitle={t("inventory.count.create_description")}
    >
      <div className="mx-auto max-w-4xl px-3 sm:px-6 lg:px-8 pb-24">
        <div className="mt-8 bg-white shadow-sm rounded-lg p-6">
          <div className="space-y-6">
            <div>
              <TextField
                fullWidth
                label={t("inventory.count.title")}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                variant="outlined"
              />
            </div>

            <div>
              <TextField
                fullWidth
                label={t("inventory.count.description")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={3}
                variant="outlined"
              />
            </div>

            <div>
              <TextField
                fullWidth
                type="date"
                label={t("inventory.count.date")}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                InputLabelProps={{
                  shrink: true,
                }}
                variant="outlined"
              />
            </div>

            <div>
              <FormControl fullWidth>
                <InputLabel>{t("inventory.count.storage")}</InputLabel>
                <Select
                  value={storageId}
                  onChange={(e) => setStorageId(e.target.value)}
                  label={t("inventory.count.storage")}
                >
                  <MenuItem value="">
                    <em>{t("inventory.count.no_storage")}</em>
                  </MenuItem>
                  {storages.map((storage) => (
                    <MenuItem key={storage.id} value={storage.id}>
                      {storage.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className="flex items-center gap-4 pt-4 border-t">
              <Button
                variant="outlined"
                startIcon={<DocumentDuplicateIcon className="h-5 w-5" />}
                onClick={() => setCopyDialogOpen(true)}
              >
                {t("inventory.count.copy_from_existing")}
              </Button>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="outlined"
                onClick={() => navigate("/dashboard/inventory/counts")}
                disabled={isLoading}
              >
                {t("Cancel")}
              </Button>
              <Button
                variant="contained"
                onClick={handleCreate}
                disabled={isLoading || !title.trim() || !date}
              >
                {isLoading ? t("Creating...") : t("inventory.count.create")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Copy Dialog */}
      <Dialog open={copyDialogOpen} onClose={() => setCopyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("inventory.count.copy_options")}</DialogTitle>
        <DialogContent>
          <div className="space-y-4 mt-4">
            <FormControl fullWidth>
              <InputLabel>{t("inventory.count.copy_from_count")}</InputLabel>
              <Select
                value={copyFromCountId}
                onChange={(e) => {
                  setCopyFromCountId(e.target.value);
                  setCopyAllProducts(false);
                }}
                label={t("inventory.count.copy_from_count")}
              >
                <MenuItem value="">
                  <em>{t("inventory.count.none")}</em>
                </MenuItem>
                {counts.map((count) => (
                  <MenuItem key={count.id} value={count.id}>
                    {count.count_number} - {count.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <div className="text-sm text-gray-600">
              {t("inventory.count.copy_all_products_info")}
            </div>
            
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                setCopyAllProducts(true);
                setCopyFromCountId("");
              }}
            >
              {t("inventory.count.copy_all_products_from_storage")}
            </Button>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyDialogOpen(false)}>
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setCopyDialogOpen(false);
              handleCreate();
            }}
            disabled={isLoading || !title.trim() || !date}
          >
            {t("inventory.count.create_and_copy")}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardHeader>
  );
};

